import { ComponentFactoryResolver, Injectable, ViewContainerRef } from '@angular/core';
import { ModalConfig } from './modal-config';
import { ModalBuilder } from './modal-builder';
import { ModalViewComponent } from './generic-modal-view/modal-view/modal-view.component';
import { Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private activeModalRef?: any; // Track the currently open modal

  constructor(private resolver: ComponentFactoryResolver) { }

  openModal(config: ModalConfig, container: ViewContainerRef): void {
    //this.resolver.resolveComponentFactory(...) creates a component factory that can be used to create instances of the ModalViewComponent dynamically at runtime. This is a way to dynamically load components into the view.
    const componentFactory = this.resolver.resolveComponentFactory(ModalViewComponent);
    // Clear previous modal if any
    container.clear();
    // Create the modal component and insert it into the container
    const componentRef: any = container.createComponent(componentFactory);
    // Store a reference to the currently active modal component
    this.activeModalRef = componentRef;
    // Pass the modal configuration to the modal component instance
    componentRef.instance.config = config;
    // Subscribe to the modal's 'close' event and destroy the component when triggered
    componentRef.instance.close.subscribe(() => componentRef.destroy());
  }

  closeModal(): void {
    // Destroy the modal and reset the reference
    if (this.activeModalRef) {
      this.activeModalRef.destroy();
      this.activeModalRef = null;
    }
  }

  createModalConfig(modalType: string, mode: string, data: any): ModalConfig {
    let builder = new ModalBuilder();

    switch (modalType) {
      case 'add-student':
        return builder
          .setTitle({
            addKey: 'user-management.students.addNew',
            editKey: 'user-management.students.info',
            viewKey: 'user-management.students.info',
            defaultKey: ''
          })

          .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: true })
          .setMode(mode)
          .setData(data)
          .setContent('student')
          .setShowFooter(false)
          // .setActions([
          //   { label: 'Save', action: () => console.log('Student saved!') },
          //   { label: 'Cancel', action: () => console.log('Cancelled') },
          // ])
          // .setFormFields([
          //   { name: 'name', validators: [Validators.required], translationKey: 'user-management.students.student-name' },
          //   { name: 'national_id', validators: [Validators.required, Validators.pattern('[a-zA-Z0-9]*')], translationKey: 'user-management.students.national-id' },
          //   { name: 'gender', validators: [], translationKey: 'user-management.students.gender' },
          //   { name: 'grade_id', validators: [Validators.required], translationKey: 'user-management.students.grade' },
          //   { name: 'class_id', validators: [Validators.required], translationKey: 'user-management.students.class' },
          //   { name: 'diseases', validators: [] , translationKey: 'user-management.students.allergies'},
          //   { name: 'allergies', validators: [] , translationKey: 'user-management.students.allergies'},
          // ])
          .build();

      case 'add-parent':
        return builder
          .setTitle({
            addKey: '',
            editKey: 'edit-parent.edit-parent',
            viewKey: 'edit-parent.parent-info',
            defaultKey: ''
          })
          .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: true })
          .setHeaderSaveBtn({ label: 'edit-parent.save', class: 'brBtn brBtn-primary', visible: true })
          .setHeaderActivatedBtn({ label: 'edit-parent.activate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setHeaderDeactivatedBtn({ label: 'edit-parent.deactivate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setFitContent(true)
          .setMode(mode)
          .setData(data)
          .setContent('parent')
          .setShowFooter(false)
          .build();
      // creating a parent for an existing student — a flow of its own, kept
      // apart from the 'add-parent' case above, which edits/views one
      case 'create-parent':
        return builder
          .setTitle({
            addKey: 'add-parent-flow.title',
            editKey: '',
            viewKey: '',
            defaultKey: 'add-parent-flow.title'
          })
          .setFitContent(true)
          .setMode(mode)
          .setData(data)
          .setContent('create-parent')
          .setShowFooter(false)
          .build();

      case 'add-assistant':
        return builder
          .setTitle({
            addKey: '',
            editKey: 'edit-assistant.edit-assistant',
            viewKey: 'edit-assistant.assistant-info',
            defaultKey: ''
          })
          .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: false })
          .setHeaderActivatedBtn({ label: 'edit-parent.activate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setHeaderDeactivatedBtn({ label: 'edit-parent.deactivate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setMode(mode)
          .setData(data)
          .setContent('assistant')
          .setShowFooter(false)
          .build();

      case 'assign-assistant':
        return builder
          .setTitle({
            addKey: 'table-view.add-assistant',
            editKey: '',
            viewKey: '',
            defaultKey: ''
          })
          .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: false })
          .setHeaderActivatedBtn({ label: 'edit-parent.activate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setHeaderDeactivatedBtn({ label: 'edit-parent.deactivate', class: 'brBtn brBtn-transparent-blue', visible: false })
          .setMode(mode)
          .setData(data)
          .setContent('assign-assistant')
          .setShowFooter(false)
          .build();

          case 'export-staff':
            return builder
              .setTitle({
                addKey: '',
                editKey: '',
                viewKey: 'staff-export-modal.title',
                defaultKey: ''
              })
              .showTitletHint(true)
              .setTitleHint('staff-export-modal.sub-title')
              .setTitleHintColor('#475467')
              .setWidth({value: 400, unit: 'px'})
              .setHeight({value: 354, unit: 'px'})
              .setBorderRadius({value: 12, unit: 'px'})
              .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: false })
              .setHeaderActivatedBtn({ label: 'edit-parent.activate', class: 'brBtn brBtn-transparent-blue', visible: false })
              .setHeaderDeactivatedBtn({ label: 'edit-parent.deactivate', class: 'brBtn brBtn-transparent-blue', visible: false })
              .setHeaderBackgroundColor('transparent')
              .setHeaderTitleColor('#101828')
              .setHeaderBorderColor('transparent')
              .setBodyOverFlow({overflowX: 'visible', overflowY: 'visible'})
              .setMode(mode)
              .setData(data)
              .setContent('export-staff')
              .setShowFooter(false)
              .build();

      // case 'otp':
      //   return builder
      //     .setTitle({
      //       addKey: 'table-view.add-assistant',
      //       editKey: '',
      //       viewKey: '',
      //       defaultKey: ''
      //     })
      //     .setHeaderEditBtn({ label: 'user-management.edit', class: 'brBtn brBtn-primary', visible: false })
      //     .setHeaderActivatedBtn({ label: 'edit-parent.activate', class: 'brBtn brBtn-transparent-blue', visible: false })
      //     .setHeaderDeactivatedBtn({ label: 'edit-parent.deactivate', class: 'brBtn brBtn-transparent-blue', visible: false })
      //     .setMode(mode)
      //     .setData(data)
      //     .setContent('otp')
      //     .setShowFooter(false)
      //     .build();
      default:
        return builder
          // .setTitle('Default Modal')
          .setContent('This is a default modal.')
          .setMode(mode)
          .setData(data)
          .setActions([{ label: 'Close', action: () => console.log('Closed') }])
          .build();
    }
  }
}
